/**
 * Footer Component
 *
 * Reusable footer with links to privacy, terms, and contact.
 * Used across all pages for consistent navigation.
 */

import { Link } from 'react-router-dom';
import { Keyboard } from 'lucide-react';
import { useState } from 'react';

interface FooterProps {
  /**
   * Whether to show keyboard shortcuts help.
   * Only shown on authenticated app pages.
   */
  showKeyboardShortcuts?: boolean;
}

export function Footer({ showKeyboardShortcuts = false }: FooterProps) {
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);

  return (
    <footer className="py-8 px-3 sm:px-4 border-t border-border-light mt-auto">
      <div className="mx-auto max-w-3xl">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          {/* Links and keyboard shortcuts */}
          <div className="flex items-center gap-6 text-sm text-text-secondary">
            <Link to="/privacy" className="hover:text-text-primary transition-colors">
              Privacy
            </Link>
            <Link to="/terms" className="hover:text-text-primary transition-colors">
              Terms
            </Link>
            <Link to="/contact" className="hover:text-text-primary transition-colors">
              Contact
            </Link>
            {showKeyboardShortcuts && (
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowShortcutsHelp(!showShortcutsHelp)}
                  className="p-1.5 text-text-tertiary hover:text-text-secondary rounded-lg hover:bg-bg-secondary transition-colors"
                  title="Keyboard shortcuts"
                  aria-label="Show keyboard shortcuts"
                  aria-expanded={showShortcutsHelp}
                >
                  <Keyboard className="w-4 h-4" />
                </button>
                {showShortcutsHelp && (
                  <>
                    <div
                      className="fixed inset-0"
                      onClick={() => setShowShortcutsHelp(false)}
                      aria-hidden="true"
                    />
                    <div className="absolute bottom-full right-0 mb-2 p-3 rounded-lg bg-bg-primary border border-border-light shadow-warm-md text-left min-w-[140px] z-50">
                      <p className="text-xs font-medium text-text-secondary mb-2">Shortcuts</p>
                      <div className="space-y-1.5 text-xs text-text-tertiary">
                        <div className="flex items-center justify-between gap-4">
                          <span>New entry</span>
                          <kbd className="px-1.5 py-0.5 rounded bg-bg-tertiary font-mono text-[10px]">N</kbd>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span>Search</span>
                          <kbd className="px-1.5 py-0.5 rounded bg-bg-tertiary font-mono text-[10px]">/</kbd>
                        </div>
                        <div className="flex items-center justify-between gap-4">
                          <span>Close / Blur</span>
                          <kbd className="px-1.5 py-0.5 rounded bg-bg-tertiary font-mono text-[10px]">Esc</kbd>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Attribution */}
          <p className="text-sm text-text-tertiary">
            Made with ❤️ in Amsterdam
          </p>
        </div>
      </div>
    </footer>
  );
}
